import React, { useRef, useEffect, useCallback, useState } from 'react';

/**
 * ResponsiveTableWrapper
 *
 * Touch-optimized table container for mobile devices.
 * Features:
 * - Horizontal swipe scrolling with momentum
 * - Scroll shadow indicators showing more content
 * - Prevents vertical scroll interference
 * - Optional mobile card view
 *
 * @example
 * ```tsx
 * <ResponsiveTableWrapper>
 *   <table>...</table>
 * </ResponsiveTableWrapper>
 * ```
 */

interface ResponsiveTableWrapperProps {
  children: React.ReactNode;
  mobileCardRender?: (rowData: unknown, index: number) => React.ReactNode;
  className?: string;
  /** Show scroll shadow indicators (default: true) */
  showScrollIndicators?: boolean;
  /** Minimum width before scroll is needed (default: 640px) */
  minWidth?: number;
}

export const ResponsiveTableWrapper: React.FC<ResponsiveTableWrapperProps> = ({
  children,
  mobileCardRender,
  className = '',
  showScrollIndicators = true,
  minWidth = 640,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  // Update scroll indicators based on scroll position
  const updateScrollIndicators = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const canScrollLeft = scrollLeft > 5;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 5;

    setScrollState((prev) => {
      if (prev.canScrollLeft !== canScrollLeft || prev.canScrollRight !== canScrollRight) {
        return { canScrollLeft, canScrollRight };
      }
      return prev;
    });
  }, []);

  // Set up scroll listener
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Initial check
    updateScrollIndicators();

    // Listen for scroll events
    el.addEventListener('scroll', updateScrollIndicators, { passive: true });

    // Listen for resize to recalculate
    const resizeObserver = new ResizeObserver(updateScrollIndicators);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollIndicators);
      resizeObserver.disconnect();
    };
  }, [updateScrollIndicators]);

  return (
    <div className={className}>
      {/* Desktop/Tablet: Scrollable table with touch optimization */}
      <div className="relative">
        {/* Scroll shadow indicators */}
        {showScrollIndicators && (
          <>
            <div
              className={`
                absolute left-0 top-0 bottom-0 w-6 z-10
                pointer-events-none
                bg-gradient-to-r from-dark-bg to-transparent
                transition-opacity duration-150
                ${scrollState.canScrollLeft ? 'opacity-100' : 'opacity-0'}
              `}
              aria-hidden="true"
            />
            <div
              className={`
                absolute right-0 top-0 bottom-0 w-6 z-10
                pointer-events-none
                bg-gradient-to-l from-dark-bg to-transparent
                transition-opacity duration-150
                ${scrollState.canScrollRight ? 'opacity-100' : 'opacity-0'}
              `}
              aria-hidden="true"
            />
          </>
        )}

        {/* Scrollable container */}
        <div
          ref={scrollRef}
          className="
            overflow-x-auto overflow-y-hidden
            overscroll-x-contain overscroll-y-none
            scroll-x-touch
          "
          style={{
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-x',
            minWidth: 0,
          }}
          data-scroll-left={scrollState.canScrollLeft}
          data-scroll-right={scrollState.canScrollRight}
        >
          <div style={{ minWidth: `${minWidth}px` }}>
            {children}
          </div>
        </div>
      </div>

      {/* Mobile: Card view (if provided) */}
      {mobileCardRender && (
        <div className="md:hidden space-y-3">
          <div className="text-xs text-dark-muted text-center p-4">
            Mobile card view
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * MobileTableScroll
 *
 * Lightweight horizontal scroll wrapper for tables.
 * Use when you need just the scroll behavior without card views.
 */
interface MobileTableScrollProps {
  children: React.ReactNode;
  className?: string;
}

export const MobileTableScroll: React.FC<MobileTableScrollProps> = ({
  children,
  className = '',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={scrollRef}
      className={`
        table-scroll-container
        ${hasOverflow ? 'pr-4' : ''}
        ${className}
      `}
      style={{
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-x',
      }}
    >
      {children}
    </div>
  );
};
